import { Injectable } from '@angular/core';
import { EnvelopeSortField } from 'src/app/enum/envelope-sort-field';
import { SentEnvelopeStatus } from 'src/app/enum/sent-envelope-status';
import { SortOrder } from 'src/app/enum/sort-order';

export interface SentEnvelopeSortSelection {
  sortField: EnvelopeSortField;
  sortOrder: SortOrder;
}

interface SentEnvelopeSortStorage {
  [status: string]: SentEnvelopeSortSelection;
}

@Injectable({
  providedIn: 'root'
})
export class SentEnvelopeSortStorageService {
  private readonly storageKeyPrefix = 'signer.sentEnvelope.sorting';
  private readonly userStorageName = 'satelittiUser';

  getForStatus(status: SentEnvelopeStatus): SentEnvelopeSortSelection {
    const storedSelection = this.getStoredSelection(status);

    if (this.isValidSelection(status, storedSelection)) {
      return storedSelection;
    }

    return this.getDefaultSelection();
  }

  setForStatus(status: SentEnvelopeStatus, selection: SentEnvelopeSortSelection): void {
    if (!this.isValidSelection(status, selection)) {
      return;
    }

    try {
      const storage = this.getStorage();
      storage[status] = selection;
      localStorage.setItem(this.getStorageKey(), JSON.stringify(storage));
    } catch {
      return;
    }
  }

  getDefaultSelection(): SentEnvelopeSortSelection {
    return {
      sortField: EnvelopeSortField.title,
      sortOrder: SortOrder.Ascending
    };
  }

  private getStoredSelection(status: SentEnvelopeStatus): SentEnvelopeSortSelection {
    const storage = this.getStorage();
    return storage[status];
  }

  private getStorage(): SentEnvelopeSortStorage {
    try {
      const storedValue = localStorage.getItem(this.getStorageKey());
      const storage = storedValue ? JSON.parse(storedValue) : {};
      return storage && typeof storage === 'object' && !Array.isArray(storage) ? storage : {};
    } catch {
      return {};
    }
  }

  private isValidSelection(
    status: SentEnvelopeStatus,
    selection: SentEnvelopeSortSelection
  ): selection is SentEnvelopeSortSelection {
    if (!selection) {
      return false;
    }

    if (!this.isValidSortOrder(selection.sortOrder) || !this.isValidSortField(selection.sortField)) {
      return false;
    }

    return selection.sortField !== EnvelopeSortField.conclutionDate ||
      status === SentEnvelopeStatus.completed;
  }

  private isValidSortOrder(sortOrder: SortOrder): boolean {
    return sortOrder === SortOrder.Ascending || sortOrder === SortOrder.Descending;
  }

  private isValidSortField(sortField: EnvelopeSortField): boolean {
    return sortField === EnvelopeSortField.title ||
      sortField === EnvelopeSortField.creationDate ||
      sortField === EnvelopeSortField.conclutionDate;
  }

  private getStorageKey(): string {
    const tenantId = localStorage.getItem('tenantId') || 'defaultTenant';
    const userId = this.getUserId();

    return `${this.storageKeyPrefix}.${tenantId}.${userId}`;
  }

  private getUserId(): string {
    try {
      const storedUser = localStorage.getItem(this.userStorageName);
      const user = storedUser ? JSON.parse(atob(storedUser)) : null;
      return user?.id?.toString() || 'defaultUser';
    } catch {
      return 'defaultUser';
    }
  }
}
